import { serverMessage } from "../../../../utils/serverMessage";
import { statusOf } from "../httpStatus";
import { downloadCourse } from "./downloads.service";
import type {
  DownloadCourse,
  DownloadCourseResult,
  DownloadParts,
} from "./downloads.types";
import type { DownloadRowState } from "./rowResult";

// A run only needs each course's ID.
type CourseRef = Pick<DownloadCourse, "courseId">;

export interface DownloadRunOptions {
  courses: CourseRef[];
  parts: DownloadParts;
  onUpdate: (courseId: number, state: DownloadRowState) => void;
  // Checked before each course starts; courses already in flight always finish.
  shouldStop: () => boolean;
  maxInFlight?: number;
  // So tests never wait: how a run sleeps and the jitter added to each retry.
  sleep?: (ms: number) => Promise<void>;
  jitterMs?: () => number;
}

const MAX_IN_FLIGHT = 2;
// Google's truncated exponential backoff, as in the upload hub: 2^n seconds
// plus up to one second of jitter, never more than 64 seconds, six retries.
const RETRY_DELAYS_MS = [2000, 4000, 8000, 16000, 32000, 64000];
const MAX_BACKOFF_MS = 64000;
const MAX_JITTER_MS = 1000;
const TRANSIENT_STATUSES = [429, 500, 502, 503, 504];

interface RunContext {
  parts: DownloadParts;
  onUpdate: DownloadRunOptions["onUpdate"];
  sleep: (ms: number) => Promise<void>;
  jitterMs: () => number;
}

const defaultSleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

// A rate limit or a server error may pass by itself, and so may a request that
// got no response (network failure, timeout); anything else would fail again.
const isTransient = (error: unknown): boolean => {
  const status = statusOf(error);
  return status === undefined || TRANSIENT_STATUSES.includes(status);
};

const retryNote = (error: unknown, waitMs: number): string => {
  const reason =
    statusOf(error) === 429
      ? "Google limitó las solicitudes"
      : "Falló una solicitud al servidor";
  return `${reason}; reintento en ${Math.ceil(waitMs / 1000)} s.`;
};

async function downloadWithRetries(
  course: CourseRef,
  ctx: RunContext,
): Promise<DownloadCourseResult> {
  for (let retry = 0; ; retry += 1) {
    try {
      return await downloadCourse(course.courseId, ctx.parts);
    } catch (error) {
      if (retry >= RETRY_DELAYS_MS.length || !isTransient(error)) throw error;
      const waitMs = Math.min(
        RETRY_DELAYS_MS[retry] + ctx.jitterMs(),
        MAX_BACKOFF_MS,
      );
      ctx.onUpdate(course.courseId, {
        kind: "running",
        note: retryNote(error, waitMs),
      });
      await ctx.sleep(waitMs);
      ctx.onUpdate(course.courseId, { kind: "running", note: null });
    }
  }
}

// Never rejects: whatever goes wrong with a course is that course's state.
async function processCourse(
  course: CourseRef,
  ctx: RunContext,
): Promise<void> {
  const { courseId } = course;
  ctx.onUpdate(courseId, { kind: "running" });
  try {
    const result = await downloadWithRetries(course, ctx);
    ctx.onUpdate(courseId, {
      kind: "done",
      od: result.od,
      quiz: result.quiz,
      // A backend that predates the video quiz neither knows nor writes it.
      quizVideo: result.quizVideo ?? "skipped",
      odSummary: result.odSummary ?? "skipped",
      trackerRowAdded: result.trackerRowAdded,
      warnings: result.warnings ?? [],
    });
  } catch (error) {
    ctx.onUpdate(courseId, {
      kind: "error",
      message: serverMessage(error, "No se pudo descargar el curso."),
    });
  }
}

// Resolves once every course is done, failed or left stopped. One course's
// failure never stops the others.
export async function runDownloads(options: DownloadRunOptions): Promise<void> {
  const ctx: RunContext = {
    parts: options.parts,
    onUpdate: options.onUpdate,
    sleep: options.sleep ?? defaultSleep,
    jitterMs: options.jitterMs ?? (() => Math.random() * MAX_JITTER_MS),
  };
  const queue = [...options.courses];

  const worker = async () => {
    while (!options.shouldStop()) {
      const course = queue.shift();
      if (!course) return;
      await processCourse(course, ctx);
    }
  };

  await Promise.all(
    Array.from({ length: options.maxInFlight ?? MAX_IN_FLIGHT }, worker),
  );
  queue.forEach((course) =>
    options.onUpdate(course.courseId, { kind: "stopped" }),
  );
}
